import { useEffect, useState } from 'react';
import { fetchJson } from '../lib/api';

const emptyDireccion = () => ({
  codigoPostal: '',
  localidad: '',
  provincia: '',
  direccion: '',
});

const emptyItem = () => ({
  cantidad: 1,
  pesoKg: '',
  altoCm: '',
  anchoCm: '',
  largoCm: '',
  tipoModelo: '',
});

export default function Cotizador({
  defaultClienteId = '',
  hideClienteSelector: _hideClienteSelector = false,
  publicMode: _publicMode = false,
}) {
  const [form, setForm] = useState({
    clienteId: defaultClienteId ? String(defaultClienteId) : '',
    origen: emptyDireccion(),
    destino: emptyDireccion(),
    items: [emptyItem()],
    esRetiro: false,
    factorAforo: 2857,
    valorDeclarado: '',
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (defaultClienteId) {
      setForm((prev) => ({
        ...prev,
        clienteId: String(defaultClienteId),
      }));
    }
  }, [defaultClienteId]);

  const setField = (section, field, value) => {
    setForm((prev) => ({
      ...prev,
      [section]: { ...prev[section], [field]: value },
    }));
  };

  const setItem = (index, field, value) => {
    setForm((prev) => {
      const items = [...prev.items];
      items[index] = { ...items[index], [field]: value };
      return { ...prev, items };
    });
  };

  const addItem = () => setForm((prev) => ({ ...prev, items: [...prev.items, emptyItem()] }));
  const removeItem = (index) =>
    setForm((prev) => ({ ...prev, items: prev.items.filter((_, i) => i !== index) }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setResult(null);
    setError(null);

    const payload = {
      clienteId: form.clienteId ? Number(form.clienteId) : null,
      origen: form.origen,
      destino: form.destino,
      items: form.items.map((item) => ({
        cantidad: Number(item.cantidad) || 1,
        pesoKg: Number(item.pesoKg) || 0,
        altoCm: Number(item.altoCm) || 0,
        anchoCm: Number(item.anchoCm) || 0,
        largoCm: Number(item.largoCm) || 0,
        tipoModelo: item.tipoModelo || null,
      })),
      valorDeclarado: form.valorDeclarado === '' ? null : Number(form.valorDeclarado),
      esRetiro: form.esRetiro,
      factorAforo: Number(form.factorAforo) || 2857,
    };

    try {
      const data = await fetchJson('/cotizador/cotizar', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      setResult(data);
    } catch (err) {
      setError(err.message || 'Error al cotizar');
    } finally {
      setLoading(false);
    }
  };

  const totals = result?.totales || {};
  const fleteBase = Number(totals.fleteBase ?? totals.total ?? 0);
  const valorDeclaradoResultado = Number(totals.valorDeclarado ?? form.valorDeclarado ?? 0);
  const seguroMonto = Number(totals.seguroMonto ?? 0);
  const subtotalEnvioSeguro = Number(totals.subtotalEnvioSeguro ?? fleteBase + seguroMonto);
  const ivaPorcentaje = Number(totals.ivaPorcentaje ?? 0);
  const ivaMonto = Number(totals.ivaMonto ?? 0);
  const totalFinal = Number(totals.total ?? subtotalEnvioSeguro + ivaMonto);

  return (
    <section className="panel quote-panel">
      <div className="panel-head">
        <div>
          <p className="eyebrow">Cotizacion real</p>
          <h2>Calcula un envio con la base activa</h2>
          <p className="panel-copy">
            El formulario llama a la API real y usa la tarifa maestra activa del cliente. Sin datos mock.
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="quote-form">
        {!_hideClienteSelector ? <section className="form-section">
          <h3>Cliente</h3>
          <label>
            Cliente ID (opcional)
            <input
              type="number"
              value={form.clienteId}
              onChange={(e) => setForm((prev) => ({ ...prev, clienteId: e.target.value }))}
              placeholder="Dejar vacío para tarifa genérica"
            />
          </label>
        </section> : null}

        <section className="form-section">
          <h3>Origen</h3>
          <div className="grid-2">
            <label>
              CP
              <input
                value={form.origen.codigoPostal}
                onChange={(e) => setField('origen', 'codigoPostal', e.target.value)}
              />
            </label>
            <label>
              Localidad
              <input
                value={form.origen.localidad}
                onChange={(e) => setField('origen', 'localidad', e.target.value)}
              />
            </label>
            <label>
              Provincia
              <input
                value={form.origen.provincia}
                onChange={(e) => setField('origen', 'provincia', e.target.value)}
              />
            </label>
            <label>
              Direccion
              <input
                value={form.origen.direccion}
                onChange={(e) => setField('origen', 'direccion', e.target.value)}
              />
            </label>
          </div>
        </section>

        <section className="form-section">
          <h3>Destino</h3>
          <div className="grid-2">
            <label>
              CP
              <input
                value={form.destino.codigoPostal}
                onChange={(e) => setField('destino', 'codigoPostal', e.target.value)}
              />
            </label>
            <label>
              Localidad
              <input
                value={form.destino.localidad}
                onChange={(e) => setField('destino', 'localidad', e.target.value)}
              />
            </label>
            <label>
              Provincia
              <input
                value={form.destino.provincia}
                onChange={(e) => setField('destino', 'provincia', e.target.value)}
              />
            </label>
            <label>
              Direccion
              <input
                value={form.destino.direccion}
                onChange={(e) => setField('destino', 'direccion', e.target.value)}
              />
            </label>
          </div>
        </section>

        <section className="form-section">
          <h3>Items</h3>
          {form.items.map((item, idx) => (
            <div key={idx} className="item-row">
              <label>
                Cant.
                <input
                  type="number"
                  min="1"
                  value={item.cantidad}
                  onChange={(e) => setItem(idx, 'cantidad', e.target.value)}
                />
              </label>
              <label>
                Peso kg
                <input
                  type="number"
                  step="0.01"
                  value={item.pesoKg}
                  onChange={(e) => setItem(idx, 'pesoKg', e.target.value)}
                />
              </label>
              <label>
                Alto cm
                <input
                  type="number"
                  step="0.01"
                  value={item.altoCm}
                  onChange={(e) => setItem(idx, 'altoCm', e.target.value)}
                />
              </label>
              <label>
                Ancho cm
                <input
                  type="number"
                  step="0.01"
                  value={item.anchoCm}
                  onChange={(e) => setItem(idx, 'anchoCm', e.target.value)}
                />
              </label>
              <label>
                Largo cm
                <input
                  type="number"
                  step="0.01"
                  value={item.largoCm}
                  onChange={(e) => setItem(idx, 'largoCm', e.target.value)}
                />
              </label>
              <label>
                Tipo
                <select value={item.tipoModelo} onChange={(e) => setItem(idx, 'tipoModelo', e.target.value)}>
                  <option value="">AUTO</option>
                  <option value="PAQUETE">PAQUETE</option>
                  <option value="BIGGER">BIGGER</option>
                  <option value="PALLET">PALLET</option>
                </select>
              </label>
              {form.items.length > 1 && (
                <button type="button" className="btn-secondary btn-inline" onClick={() => removeItem(idx)}>
                  Eliminar
                </button>
              )}
            </div>
          ))}

          <button type="button" className="btn-secondary btn-inline" onClick={addItem}>
            + Agregar item
          </button>
        </section>

        <section className="form-section quote-declared-section">
          <div>
            <h3>Valor declarado</h3>
            <p className="panel-copy">Indicá el valor de la mercadería para calcular el seguro del envío.</p>
          </div>
          <label>
            Valor declarado del envío
            <input
              type="number"
              min="0"
              step="0.01"
              value={form.valorDeclarado}
              onChange={(e) => setForm((prev) => ({ ...prev, valorDeclarado: e.target.value }))}
              placeholder="Ej.: 100000"
            />
            <small>Seguro: 2% del valor declarado.</small>
          </label>
        </section>

        <button type="submit" className="btn-primary" disabled={loading}>
          {loading ? 'Cotizando...' : 'Cotizar'}
        </button>
      </form>

      {error && <div className="alert error">{error}</div>}

      {result && (
        <div className="result-panel">
          <div className="result-summary">
            <div>
              <p className="eyebrow">Detalle del envío</p>
              <h3>{result.clienteNombre || (result.clienteId ? `Cliente ${result.clienteId}` : 'Tarifa genérica')}</h3>
            </div>
            <div className="result-total">
              <span>Total con IVA</span>
              <strong>${totalFinal.toFixed(2)}</strong>
            </div>
          </div>
          <div className="quote-breakdown" aria-label="Desglose del precio del envío">
            <div className="quote-breakdown-row">
              <span>Envío</span>
              <strong>${fleteBase.toFixed(2)}</strong>
            </div>
            <div className="quote-breakdown-row">
              <span>Seguro ({Number(totals.seguroPorcentaje ?? 2).toFixed(2)}% de ${valorDeclaradoResultado.toFixed(2)})</span>
              <strong>${seguroMonto.toFixed(2)}</strong>
            </div>
            <div className="quote-breakdown-row quote-breakdown-row--subtotal">
              <span>Envío + seguro</span>
              <strong>${subtotalEnvioSeguro.toFixed(2)}</strong>
            </div>
            <div className="quote-breakdown-row">
              <span>IVA ({ivaPorcentaje.toFixed(2)}%)</span>
              <strong>${ivaMonto.toFixed(2)}</strong>
            </div>
            <div className="quote-breakdown-row quote-breakdown-row--total">
              <span>Total a pagar</span>
              <strong>${totalFinal.toFixed(2)}</strong>
            </div>
          </div>

          <div className="result-grid">
            <article>
              <span>Ruta</span>
              <strong>{result.origenZona || '-'} -&gt; {result.destinoZona || '-'}</strong>
            </article>
            <article>
              <span>Peso real</span>
              <strong>{Number(result.totales?.pesoRealTotal || 0).toFixed(2)} kg</strong>
            </article>
            <article>
              <span>Peso facturable</span>
              <strong>{Number(result.totales?.pesoFacturableTotal || 0).toFixed(2)} kg</strong>
            </article>
            <article>
              <span>M3 total</span>
              <strong>{Number(result.totales?.m3Total || 0).toFixed(4)} m3</strong>
            </article>
            <article>
              <span>Bultos</span>
              <strong>{result.totales?.bultosTotal ?? 0}</strong>
            </article>
          </div>
        </div>
      )}
    </section>
  );
}
